// hooks/useBrowserLock.js
import { useEffect, useState, useCallback } from "react";

const useBrowserLock = (enabled = false, onExitRequest = null) => {
  const [showExitModal, setShowExitModal] = useState(false);
  const [showFullscreenPrompt, setShowFullscreenPrompt] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Check if fullscreen is supported
  const isFullscreenSupported = () => {
    return !!(
      document.fullscreenEnabled ||
      document.webkitFullscreenEnabled ||
      document.mozFullScreenEnabled ||
      document.msFullscreenEnabled
    );
  };

  // Check if currently in fullscreen
  const isInFullscreen = () => {
    return !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
    );
  };

  // Enter fullscreen
  const enterFullscreen = useCallback(async () => {
    if (!isFullscreenSupported()) {
      console.warn("Fullscreen not supported in this browser");
      return false;
    }

    const element = document.documentElement;
    console.log("Attempting to enter fullscreen...");

    try {
      if (element.requestFullscreen) {
        await element.requestFullscreen();
        console.log("Successfully entered fullscreen");
        setIsFullscreen(true);
        return true;
      } else if (element.webkitRequestFullscreen) {
        await element.webkitRequestFullscreen();
        console.log("Successfully entered fullscreen (webkit)");
        setIsFullscreen(true);
        return true;
      } else if (element.mozRequestFullScreen) {
        await element.mozRequestFullScreen();
        console.log("Successfully entered fullscreen (moz)");
        setIsFullscreen(true);
        return true;
      } else if (element.msRequestFullscreen) {
        await element.msRequestFullscreen();
        console.log("Successfully entered fullscreen (ms)");
        setIsFullscreen(true);
        return true;
      }
    } catch (error) {
      console.warn("Failed to enter fullscreen:", error);
      return false;
    }
  }, []);

  // Exit fullscreen
  const exitFullscreen = useCallback(async () => {
    if (!isFullscreenSupported()) return;

    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        await document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen();
      }
      setIsFullscreen(false);
    } catch (error) {
      console.warn("Failed to exit fullscreen:", error);
    }
  }, []);

  // Handle ESC key press
  const handleKeyDown = useCallback(
    (event) => {
      console.log(
        "ESC key pressed, enabled:",
        enabled,
        "isInFullscreen:",
        isInFullscreen()
      );
      if (event.key === "Escape" && enabled) {
        console.log("ESC pressed while browser lock enabled");
        event.preventDefault();
        event.stopPropagation();
        setShowExitModal(true);
      }
    },
    [enabled]
  );

  // Enter fullscreen when enabled
  useEffect(() => {
    console.log("useBrowserLock effect triggered, enabled:", enabled);

    if (enabled) {
      // Try to enter fullscreen automatically first
      const timer = setTimeout(async () => {
        console.log("Timer triggered, calling enterFullscreen");
        const success = await enterFullscreen();

        // If automatic fullscreen fails, show prompt for user interaction
        if (!success) {
          console.log("Automatic fullscreen failed, showing prompt");
          setShowFullscreenPrompt(true);
        }
      }, 100);

      return () => clearTimeout(timer);
    } else {
      // Exit fullscreen when disabled
      console.log("Browser lock disabled, exiting fullscreen");
      exitFullscreen();
      setShowFullscreenPrompt(false);
    }
  }, [enabled, enterFullscreen, exitFullscreen]);

  // Add ESC key listener and fullscreen change listener
  useEffect(() => {
    if (enabled) {
      const handleFullscreenChange = () => {
        const fullscreen = isInFullscreen();
        console.log("Fullscreen change detected, isInFullscreen:", fullscreen);
        setIsFullscreen(fullscreen);
        if (fullscreen) {
          setShowFullscreenPrompt(false);
        } else {
          // If we exit fullscreen while browser lock is enabled, show exit modal
          if (enabled && !fullscreen) {
            console.log(
              "Exited fullscreen while browser lock enabled, showing exit modal"
            );
            setShowExitModal(true);
          }
        }
      };

      // Use capture: true to catch ESC key before browser handles it
      document.addEventListener("keydown", handleKeyDown, { capture: true });
      document.addEventListener("fullscreenchange", handleFullscreenChange);
      document.addEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.addEventListener("mozfullscreenchange", handleFullscreenChange);
      document.addEventListener("MSFullscreenChange", handleFullscreenChange);

      return () => {
        document.removeEventListener("keydown", handleKeyDown, {
          capture: true,
        });
        document.removeEventListener(
          "fullscreenchange",
          handleFullscreenChange
        );
        document.removeEventListener(
          "webkitfullscreenchange",
          handleFullscreenChange
        );
        document.removeEventListener(
          "mozfullscreenchange",
          handleFullscreenChange
        );
        document.removeEventListener(
          "MSFullscreenChange",
          handleFullscreenChange
        );
      };
    }
  }, [enabled, handleKeyDown]);

  // Handle exit confirmation
  const handleExitConfirm = useCallback(() => {
    setShowExitModal(false);
    if (onExitRequest) {
      onExitRequest();
    }
  }, [onExitRequest]);

  // Handle exit cancellation
  const handleExitCancel = useCallback(() => {
    setShowExitModal(false);
    // Re-enter fullscreen if still enabled
    if (enabled) {
      enterFullscreen();
    }
  }, [enabled, enterFullscreen]);

  // Handle manual fullscreen trigger
  const handleManualFullscreen = useCallback(async () => {
    const success = await enterFullscreen();
    if (success) {
      setShowFullscreenPrompt(false);
    }
  }, [enterFullscreen]);

  // Handle fullscreen prompt dismissal
  const handleDismissPrompt = useCallback(() => {
    setShowFullscreenPrompt(false);
  }, []);

  return {
    showExitModal,
    showFullscreenPrompt,
    isFullscreen,
    handleExitConfirm,
    handleExitCancel,
    handleManualFullscreen,
    handleDismissPrompt,
  };
};

export default useBrowserLock;
